/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Image, Input, Spin, Typography
} from 'antd';
import { FineUploaderBasic } from 'fine-uploader/lib/core';
import React, { useEffect, useRef, useState } from 'react';

import Icon from '../Icon';

import { refreshTokenService } from 'common/services/authenticate';
import { getAccessToken, setAccessToken, setRefreshToken } from 'common/services/common/storage';
import { ERR_UPLOAD_RES, URL_CONST } from 'common/utils/constant';
import { compressImageFile } from 'common/utils/functions';

export type UploadFileType = {
  file?: File | string;
  pathSubmit: string;
};

type ChunkErrorResTypes = {
  detail: string;
  source: {
    pointer: string
  };
  status: number;
  title: string;
};

interface UploadPhotoProps {
  description: string,
  title?: string,
  handleChooseFile: (file: UploadFileType) => void;
  handleRemoveFile: () => void;
  handleError?: (message: string) => void;
  readOnly?: boolean;
  value?: UploadFileType;
  className?: string;
  idUpload: string;
  big?: boolean
}

const UploadPhoto: React.FC<UploadPhotoProps> = ({
  description,
  title,
  handleChooseFile,
  handleRemoveFile,
  handleError,
  readOnly,
  value,
  className,
  idUpload,
  big
}) => {
  const token = getAccessToken();
  const [preview, setPreview] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const fileRef = useRef<File | undefined>(undefined);

  const uploader = new FineUploaderBasic({
    autoUpload: false,
    request: {
      endpoint: URL_CONST.FILE_UPLOAD_CHUNK,
      customHeaders: {
        Authorization: `Bearer ${token}`,
      },
    },
    validation: {
      allowedExtensions: ['jpeg', 'jpg', 'png'],
      sizeLimit: 1000000,
    },
    chunking: {
      enabled: true,
      mandatory: true,
      partSize: 1000000, // 1MB
      success: {
        jsonPayload: true,
        endpoint: URL_CONST.FILE_MERGE_CHUNK,
        params(id: number) {
          const uuid = uploader.getUuid(id);
          const { type } = (uploader.getFile(id) as File);
          return {
            chunkedUuid: uuid,
            fileExtension: type.split('/').length > 1 ? type.split('/')[1] : type,
          };
        }
      },
    },
    callbacks: {
      onSubmit(id: number) {
        const file = uploader.getFile(id);
        if (file && file.size > 1000000) {
          if (handleError) {
            handleError('File size must be less than 1MB');
          }
          uploader.cancel(id); // Cancel upload if file is too large
          setLoading(false);
          // return false;
        }
      },
      onComplete(id: number, name: string, res: any, xhr: XMLHttpRequest) {
        const file = uploader.getFile(id);
        handleChooseFile({
          file,
          pathSubmit: (res.errors as ChunkErrorResTypes[])?.[0].status === 422
            ? ERR_UPLOAD_RES.inValidSize : res?.data?.path
        });
        setLoading(false);
        // const uuid = uploader.getUuid(id);
        // const file = uploader.getFile(id);
        // setUuidImages((prev) => [...prev,
        // { chunkedUuid: uuid, fileExtension: file.name.split('.').pop() }]);

        // if (!res.success) {
        //   const errors = JSON.parse(xhr.response).errors as ValidateError[];
        // } else {
        // }
      },
      onError: async (id: number, name: string, errorReason: string, xhr: XMLHttpRequest) => {
        setLoading(false);
        if (xhr.status === 401) {
          await refreshTokenService()
            .then((data) => {
              setAccessToken(data.accessToken);
              setRefreshToken(data.refreshToken);
              uploader.setCustomHeaders({ Authorization: `Bearer ${data.accessToken}` });
              uploader.retry(id);
            });
        }
      },
    }
  });

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e?.target?.files?.[0];

    if (file) {
      try {
        const compressedFile = await compressImageFile(file, big) as File;
        fileRef.current = compressedFile;
        setLoading(true);
        uploader.addFiles(compressedFile);
        uploader.uploadStoredFiles();
      } catch (err) {
        console.log('Upload error: ', err);
      }
    }
  };

  useEffect(() => {
    if (value && value.file) {
      setPreview(typeof value.file === 'string' ? value.file : URL.createObjectURL(value.file));
    } else if (value?.pathSubmit) {
      setPreview(value?.pathSubmit);
    } else {
      setPreview('');
    }
  }, [value]);

  return (
    <div className={`m-uploadPhoto ${className}`}>
      {
        loading && (
          <div className="m-uploadPhoto_loading">
            <Spin size="default" spinning />
          </div>
        )
      }
      {
        preview ? (
          <div className="m-uploadPhoto_wrapImage">
            {
              !readOnly && (
                <div
                  onClick={() => {
                    handleRemoveFile();
                    setPreview('');
                    fileRef.current = undefined;
                  }}
                  className="m-uploadPhoto_icon"
                >
                  <Icon iconName="clearImage" size="24" />
                </div>
              )
            }
            <Image
              src={preview}
              preview
              // ratio={100}
              alt="review"
              className="m-uploadPhoto_image"
            // extendClasses="m-uploadPhoto_image"
            />
          </div>
        ) : (
          <label htmlFor={idUpload} className="m-uploadPhoto_wrapContent">
            <Icon iconName="uploadPhoto" size="24" />
            <div className="u-mt-8">
              <Typography.Text
                className="fs-12x20"
              >
                {description}
              </Typography.Text>
            </div>
            <div className="m-uploadPhoto_file">
              <Input
                id={idUpload}
                disabled={readOnly}
                readOnly={readOnly}
                type="file"
                onChange={handleUpload}
                accept="image/*"
              />
            </div>
          </label>
        )
      }
    </div>
  );
};

export default UploadPhoto;
